import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import GeneralTile from './GeneralTile';
import MyBoards from './MyBoards';
import eventBus, { eventBusValues } from '../../eventBus';
import my_messages from '../../icons/my-messages.svg';
import my_professional_brand from '../../icons/my-professional-brand.svg';
import calendar from '../../icons/calendar.svg';
import professional_goals from '../../icons/professional_goals.svg';
import { useAccount } from '../../contexts/Account';
import LeadershipFrame from '../../components/LeadershipFrame';
import InvitationsBoard from '../../components/Advisor/InvitationsBoard';
import ProfessionalGoals from './ProfessionalGoals';
import { getSavedCompanies } from '../../services/advisor_services';

function BoardsAndInvitations() {
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const { accountDetails, isFreeTrial, isFreemium, conversations } =
    useAccount();
  const [showDetails, setShowDetails] = useState(false);
  const [showRecomendations, setShowRecomendations] = useState(false);
  const [showLeadershipFrame, setShowLeadershipFrame] = useState(false);
  const [showProfessionalGoals, setShowProfessionalGoals] = useState(false);
  const [showAdvisoryBoards, setShowAdvisoryBoards] = useState(false);
  const history = useHistory();

  const detailsRef = useRef(null);
  const mobileGoalsRef = useRef(null);
  const mobileBoardsRef = useRef(null);
  const recommendationsRef = useRef(null);

  const totalInvitations = accountDetails.boards.invitedBoards.length;
  const [savedCompanies, setSavedCompanies] = useState([]);
  const totalSaved = savedCompanies.length || 0;

  const loadSavedCompanies = () => {
    getSavedCompanies({ advisorId: accountDetails.id })
      .then((response) => {
        const companies = response.data.getSavedCompanies.filter(
          (c) =>
            c.boardOwnerStatus !== 'inactive' &&
            c.boardOwnerStatus !== 'paused' &&
            c.opportunityStage !== 'dormant' &&
            c.opportunityStage !== 'pending_cancellation' &&
            c.opportunityStage !== 'position_filled'
        );
        setSavedCompanies(companies);
      })
      .catch((error) =>
        console.error('Error fetching saved companies:', error)
      );
  };

  useEffect(() => {
    if (showDetails) {
      setTimeout(() => {
        if (isXS && showAdvisoryBoards) {
          mobileBoardsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        } else if (isXS && showProfessionalGoals) {
          mobileGoalsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        } else if (!isXS) {
          detailsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 100);
    }
  }, [showDetails]);

  useEffect(() => {
    if (showRecomendations) {
      setTimeout(() => {
        recommendationsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [showRecomendations]);

  useEffect(() => {
    loadSavedCompanies();
  }, []);

  const sections = {
    advisoryBoards: setShowAdvisoryBoards,
    thoughtLeadership: setShowProfessionalGoals,
  };

  function toggleSection(sectionKey) {
    const isCurrentlyOpen =
      sectionKey === 'advisoryBoards'
        ? showAdvisoryBoards
        : showProfessionalGoals;

    Object.values(sections).forEach((setSection) => setSection(false));
    sections[sectionKey](!isCurrentlyOpen);
    setShowDetails(!isCurrentlyOpen);
  }

  function getUnreadMessages() {
    let totalUnread = 0;
    conversations.forEach((conv) => {
      totalUnread += conv.unreadCount;
    });
    return totalUnread;
  }

  return (
    <>
      <LeadershipFrame
        showLeadershipFrame={showLeadershipFrame}
        setShowLeadershipFrame={setShowLeadershipFrame}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: isXS ? 'column' : 'row',
          justifyContent: 'space-between',
          columnGap: '20px',
          rowGap: '20px',
        }}
      >
        <GeneralTile
          ids={{
            tileID: 'my-messages-tile-id',
            buttonID: 'my-messages-button-id',
          }}
          title="My Messages"
          onClick={() =>
            eventBus.dispatch(eventBusValues.triggerConversationDrawerV2, {})
          }
          description="View messages from your boards and fellow advisors on AdvisoryCloud. Check back every month to see your new opportunities."
          image={my_messages}
          badge={getUnreadMessages()}
        />
        <GeneralTile
          ids={{
            tileID: 'my-professional-brand-tile-id',
            buttonID: 'my-professional-brand-button-id',
          }}
          title="My Professional Brand"
          onClick={() =>
            eventBus.dispatch(eventBusValues.triggerAdvisorDrawerAction, {
              ...accountDetails,
              // openInEdit: false,
            })
          }
          description="View or update your AdvisoryCloud profile to attract senior-level opportunities and valuable networking connections."
          image={my_professional_brand}
        />
        <GeneralTile
          ids={{
            tileID: 'my-recommendations-tile-id',
            buttonID: 'my-recommendations-button-id',
          }}
          title="My Recommendations"
          onClick={() => setShowRecomendations((st) => !st)}
          description="View top matches and recommendations related to board roles and other senior-level opportunities on AdvisoryCloud."
          image={professional_goals}
          badge={totalInvitations + totalSaved}
          buttonText="View"
        />
        {showRecomendations && isXS && (
          <Box
            ref={recommendationsRef}
            sx={{
              display: 'block',
              marginTop: '20px',
            }}
          >
            <InvitationsBoard />
          </Box>
        )}
        <GeneralTile
          ids={{
            tileID: 'my-advisory-boards-tile-id',
            buttonID: 'my-advisory-boards-button-id',
          }}
          title="My Advisory Boards"
          onClick={() => toggleSection('advisoryBoards')}
          description="View your current advisory boards, share your ideas, make an introduction, attend board meetings and more."
          image={calendar}
        />
        {showDetails && showAdvisoryBoards && isXS ? (
          <Box ref={mobileBoardsRef} sx={{ scrollMarginTop: '130px' }}>
            <MyBoards />
          </Box>
        ) : null}

        {showDetails && isXS ? (
          <Box ref={mobileGoalsRef} sx={{ scrollMarginTop: '130px' }}>
            {showProfessionalGoals && <ProfessionalGoals />}
          </Box>
        ) : null}
      </Box>

      {showDetails && !isXS && (
        <Box ref={detailsRef} sx={{ marginTop: '20px' }}>
          {showAdvisoryBoards && <MyBoards />}
          {!showAdvisoryBoards && showProfessionalGoals && (
            <ProfessionalGoals />
          )}
        </Box>
      )}

      {showRecomendations && !isXS && (
        <Box
          ref={recommendationsRef}
          sx={{
            display: 'block',
            marginTop: '20px',
          }}
        >
          <InvitationsBoard />
        </Box>
      )}
    </>
  );
}

export default BoardsAndInvitations;
